import * as SliderPrimitive from '@radix-ui/react-slider';
import type { FilterState } from './filter-panel';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function dayEpoch(iso: string): number {
  return Math.floor(new Date(iso).getTime() / 86_400_000);
}

function isoFromDay(day: number): string {
  return new Date(day * 86_400_000).toISOString().slice(0, 10);
}

function formatLabel(iso: string): string {
  return new Intl.DateTimeFormat('nb-NO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface DateRangeBarProps {
  dateMin: string;
  dateMax: string;
  filters: FilterState;
  onChange: (filters: FilterState) => void;
}

export function DateRangeBar({ dateMin, dateMax, filters, onChange }: DateRangeBarProps) {
  if (!dateMin || !dateMax) return null;

  const min = dayEpoch(dateMin);
  const max = dayEpoch(dateMax);

  if (min >= max) return null;

  const fromDay = filters.dateFrom ? dayEpoch(filters.dateFrom) : min;
  const toDay = filters.dateTo ? dayEpoch(filters.dateTo) : max;

  const activeFrom = Math.max(min, Math.min(fromDay, max));
  const activeTo = Math.max(min, Math.min(toDay, max));

  const isFiltered = activeFrom > min || activeTo < max;

  function handleValueChange([newFrom, newTo]: number[]) {
    onChange({
      ...filters,
      dateFrom: newFrom <= min ? '' : isoFromDay(newFrom),
      dateTo: newTo >= max ? '' : isoFromDay(newTo),
    });
  }

  return (
    <div className="border-b px-4 py-2" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
      <div className="flex items-center gap-3">
        {/* Min-dato */}
        <span className="shrink-0 text-[11px] text-text-tertiary tabular-nums">
          {formatLabel(dateMin)}
        </span>

        {/* Slider */}
        <div className="relative flex-1">
          <SliderPrimitive.Root
            min={min}
            max={max}
            step={1}
            value={[activeFrom, activeTo]}
            onValueChange={handleValueChange}
            className="relative flex touch-none select-none items-center"
          >
            <SliderPrimitive.Track
              className="relative h-1 w-full grow rounded-full"
              style={{ background: 'rgba(255,255,255,0.12)' }}
            >
              <SliderPrimitive.Range
                className="absolute h-full rounded-full"
                style={{
                  background: isFiltered ? 'rgba(99,132,255,0.7)' : 'rgba(255,255,255,0.25)',
                }}
              />
            </SliderPrimitive.Track>

            {/* Thumb: start */}
            <SliderPrimitive.Thumb
              className="block h-3.5 w-3.5 rounded-full border-2 shadow-sm outline-none transition-transform duration-75 hover:scale-110 focus-visible:ring-1 focus-visible:ring-white/50"
              style={{
                background: isFiltered ? 'rgb(99,132,255)' : 'rgba(255,255,255,0.6)',
                borderColor: isFiltered ? 'rgb(140,163,255)' : 'rgba(255,255,255,0.8)',
              }}
              aria-label="Fra dato"
            />

            {/* Thumb: slutt */}
            <SliderPrimitive.Thumb
              className="block h-3.5 w-3.5 rounded-full border-2 shadow-sm outline-none transition-transform duration-75 hover:scale-110 focus-visible:ring-1 focus-visible:ring-white/50"
              style={{
                background: isFiltered ? 'rgb(99,132,255)' : 'rgba(255,255,255,0.6)',
                borderColor: isFiltered ? 'rgb(140,163,255)' : 'rgba(255,255,255,0.8)',
              }}
              aria-label="Til dato"
            />
          </SliderPrimitive.Root>

          {/* Etiketter under thumbs – bare synlige når filteret er aktivt */}
          {isFiltered && (
            <div className="relative mt-1 h-3">
              <ThumbLabel
                value={activeFrom}
                min={min}
                max={max}
                text={formatLabel(isoFromDay(activeFrom))}
                side="from"
              />
              <ThumbLabel
                value={activeTo}
                min={min}
                max={max}
                text={formatLabel(isoFromDay(activeTo))}
                side="to"
              />
            </div>
          )}
        </div>

        {/* Max-dato */}
        <span className="shrink-0 text-[11px] text-text-tertiary tabular-nums">
          {formatLabel(dateMax)}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ThumbLabel – flytende etikett under thumb
// ---------------------------------------------------------------------------

function ThumbLabel({
  value,
  min,
  max,
  text,
  side,
}: {
  value: number;
  min: number;
  max: number;
  text: string;
  side: 'from' | 'to';
}) {
  const pct = ((value - min) / (max - min)) * 100;
  const translateX = side === 'from' ? '-10%' : '-90%';

  return (
    <span
      className="absolute whitespace-nowrap text-[10px] tabular-nums text-text-secondary"
      style={{ left: `${pct}%`, transform: `translateX(${translateX})` }}
    >
      {text}
    </span>
  );
}
